import type { FC } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../../../../store/store';
import type { CartItem, Order } from '../../../../../../types/common';
import { setProductToCart, setToLocalStorage } from '../../../../../../store/UserSlice';
import { PATHS } from '../../../../../../constants/routes';
import OrderDetailsPanel from './OrderDetailsPanel';
import styles from './OrdersPanel.module.scss';

type Props = {
    orders: Order[];
    loading?: boolean;
    error?: string | null;
};
type StatusTone = 'ok' | 'warn' | 'neutral' | 'muted';

const BASE_URL = String(process.env.REACT_APP_SERVER_URL || '').trim();
// const HARD_CODED_DELIVERY_DATE = '20.09.2026';
const PAGE_SIZE = 10;

const getPageScrollTop = (): number => {
    if (typeof window === 'undefined') return 0;
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
};
const scrollPageTo = (top: number) => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top, left: 0, behavior: 'auto' });
};
const safeNum = (n: unknown, fb = 0) => (typeof n === 'number' && Number.isFinite(n) ? n : fb);
const normalizeQuantity = (value: unknown): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return Math.floor(num);
};
const collectOrderCartRequirements = (order: Order): Array<{ productId: string; quantity: number; template: CartItem }> => {
    const items = Array.isArray(order?.cart) ? order.cart : [];
    const grouped = new Map<string, { quantity: number; template: CartItem }>();
    items.forEach((item) => {
        const productId = String(item?.productId ?? '').trim();
        const quantity = normalizeQuantity(item?.quantity);
        if (!productId || quantity <= 0) return;
        const existing = grouped.get(productId);
        if (existing) {
            existing.quantity += quantity;
            return;
        }
        grouped.set(productId, { quantity, template: item });
    });
    return Array.from(grouped, ([productId, value]) => ({ productId, quantity: value.quantity, template: value.template }));
};
const normalizeTimestamp = (value: unknown): number | null => {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num < 1_000_000_000_000 ? num * 1000 : num;
};
const formatOrderDate = (value: unknown): string => {
    try {
        const ts = normalizeTimestamp(value);
        if (!ts) return '—';
        return new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(new Date(ts));
    } catch {
        return '—';
    }
};
const toMoney = (value: unknown): string => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount.toLocaleString('ru-RU') : '0';
};
const getOrderKey = (order: Partial<Order> | null | undefined): string => String(order?.id ?? (order as any)?._id ?? order?.orderNumber ?? 'order');
const getStatusMeta = (order: Order): { label: string; tone: StatusTone } => {
    const raw = String(order?.orderStatus ?? '')
        .trim()
        .toLowerCase();
    const isPickup = !order?.delivery || !order?.delivery?.method;
    if (raw === 'paid') return { label: 'Собирается', tone: 'neutral' };
    if (raw === 'assembled') return { label: isPickup ? 'Готов к выдаче' : 'Собран', tone: 'neutral' };
    if (raw === 'indelivery' || raw === 'in_delivery') return { label: 'Доставляется', tone: 'neutral' };
    if (raw === 'completed') return { label: 'Доставлен', tone: 'ok' };
    if (raw === 'pending') return { label: 'Ждет оплаты', tone: 'warn' };
    if (raw === 'cancelled' || raw === 'canceled') return { label: 'Отменен', tone: 'muted' };
    return { label: 'Статус уточняется', tone: 'muted' };
};
const isPendingOrder = (order: Order): boolean => {
    return (
        String(order?.orderStatus ?? '')
            .trim()
            .toLowerCase() === 'pending'
    );
};
const buildProductImageUrl = (imageId: string): string => {
    if (!BASE_URL || !imageId) return '';
    return `${BASE_URL}/products/images/${encodeURIComponent(imageId)}`;
};
const readImageId = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (!value || typeof value !== 'object') return '';
    const obj = value as Record<string, unknown>;
    if (typeof obj.$oid === 'string') return obj.$oid.trim();
    if (typeof obj.id === 'string') return obj.id.trim();
    if (typeof obj._id === 'string') return obj._id.trim();
    if (obj._id && typeof obj._id === 'object') {
        const nestedId = readImageId(obj._id);
        if (nestedId) return nestedId;
    }
    return '';
};
const pushImageId = (target: string[], value: unknown) => {
    const nextId = readImageId(value);
    if (!nextId) return;
    if (target.includes(nextId)) return;
    target.push(nextId);
};
const collectPrimaryImageIds = (source: unknown): string[] => {
    if (!source || typeof source !== 'object') return [];
    const obj = source as Record<string, unknown>;
    const ids: string[] = [];
    pushImageId(ids, obj.image);
    pushImageId(ids, obj.imageId);
    if (obj.images && typeof obj.images === 'object') {
        const images = obj.images as Record<string, unknown>;
        pushImageId(ids, images.image);
    }
    return ids;
};

const OrdersPanel: FC<Props> = ({ orders, loading, error }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const products = useSelector((state: RootState) => state.product.products);
    const cart = useSelector((state: RootState) => state.user.cart);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const appendLockRef = useRef(false);
    const ordersScrollTopRef = useRef(0);
    const scrollSyncReadyRef = useRef(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [brokenImageUrls, setBrokenImageUrls] = useState<Record<string, true>>({});
    const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

    const sorted = useMemo(() => {
        const list = Array.isArray(orders) ? [...orders] : [];
        return list.sort((a, b) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0));
    }, [orders]);

    const activeOrder = useMemo(() => sorted.find((order) => getOrderKey(order) === activeOrderId) ?? null, [sorted, activeOrderId]);

    useLayoutEffect(() => {
        if (!scrollSyncReadyRef.current) {
            scrollSyncReadyRef.current = true;
            return;
        }
        if (activeOrder) {
            scrollPageTo(0);
            return;
        }
        scrollPageTo(ordersScrollTopRef.current);
    }, [activeOrder]);

    const ordersCount = safeNum(sorted.length);
    const visibleOrders = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
    const hasMoreOrders = visibleCount < sorted.length;

    const cartQuantityByProductId = useMemo(() => {
        return cart.reduce((acc, item) => {
            const productId = String(item?.productId ?? '').trim();
            const quantity = normalizeQuantity(item?.quantity);
            if (!productId || quantity <= 0) return acc;
            acc.set(productId, (acc.get(productId) ?? 0) + quantity);
            return acc;
        }, new Map<string, number>());
    }, [cart]);

    const resolveOrderItemImages = useCallback(
        (item: any): string[] => {
            const productId = String(item?.productId ?? '').trim();
            const product = productId && Array.isArray(products) ? products.find((candidate: any) => String(candidate?.id ?? '').trim() === productId) : null;
            const imageIds = [...collectPrimaryImageIds(item), ...collectPrimaryImageIds(product)];
            return Array.from(new Set(imageIds)).map(buildProductImageUrl).filter(Boolean);
        },
        [products],
    );
    const handleImageError = useCallback((src: string) => {
        if (!src) return;
        setBrokenImageUrls((prev) => {
            if (prev[src]) return prev;
            return { ...prev, [src]: true };
        });
    }, []);

    useEffect(() => {
        if (loading || error || activeOrder) return;
        if (!hasMoreOrders) return;
        const node = sentinelRef.current;
        if (!node) return;
        observerRef.current?.disconnect();
        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                if (appendLockRef.current) return;
                appendLockRef.current = true;
                setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, sorted.length));
                requestAnimationFrame(() => {
                    appendLockRef.current = false;
                });
            },
            { root: null, rootMargin: '220px 0px', threshold: 0.01 },
        );
        observerRef.current.observe(node);
        return () => {
            observerRef.current?.disconnect();
        };
    }, [loading, error, hasMoreOrders, sorted.length, activeOrder]);

    const handleOpenDetails = useCallback((order: Order) => {
        ordersScrollTopRef.current = getPageScrollTop();
        setActiveOrderId(getOrderKey(order));
    }, []);
    const handleBackToOrders = useCallback(() => {
        setActiveOrderId(null);
    }, []);

    const syncOrderItemsToCart = useCallback(
        async (order: Order): Promise<string[]> => {
            const requirements = collectOrderCartRequirements(order);
            let cartChanged = false;
            requirements.forEach(({ productId, quantity, template }) => {
                const currentQuantity = cartQuantityByProductId.get(productId) ?? 0;
                if (currentQuantity >= quantity) return;
                const missingQuantity = quantity - currentQuantity;
                const weight = safeNum(template?.weight, 0);
                const price = safeNum(template?.price, 0);
                dispatch(
                    setProductToCart({
                        productId,
                        name: typeof template?.name === 'string' ? template.name : '',
                        quantity: missingQuantity,
                        weight,
                        totalWeight: weight * missingQuantity,
                        price,
                        totalPrice: price * missingQuantity,
                    } as CartItem),
                );
                cartChanged = true;
            });
            if (cartChanged) {
                await dispatch(setToLocalStorage('cart'));
            }
            return requirements.map(({ productId }) => productId);
        },
        [cartQuantityByProductId, dispatch],
    );

    const handlePrepareOrderForCart = useCallback(
        async (order: Order) => {
            const productIds = await syncOrderItemsToCart(order);
            navigate(((PATHS as Record<string, string>)?.cart || '/cart') as string, {
                state: {
                    repeatOrderSelection: {
                        productIds,
                        token: `${String(order?.id ?? order?.orderNumber ?? 'order')}-${Date.now()}`,
                    },
                },
            });
        },
        [navigate, syncOrderItemsToCart],
    );

    if (activeOrder) {
        return (
            <OrderDetailsPanel order={activeOrder} onBack={handleBackToOrders} onRepeatOrder={handlePrepareOrderForCart} onPayOrder={handlePrepareOrderForCart} />
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.head}>
                <h2 className={styles.title}>Заказы</h2>
                {ordersCount > 0 && <span className={styles.count}>{ordersCount} шт</span>}
            </div>
            {loading ? <div className={styles.muted}>Загрузка…</div> : null}
            {error ? <div className={styles.error}>{error}</div> : null}
            {!loading && !error && sorted.length === 0 ? <div className={styles.empty}>Заказов пока нет</div> : null}
            {!loading && !error && sorted.length > 0 ? (
                <>
                    <div className={styles.list} aria-label="Список заказов">
                        {visibleOrders.map((order) => {
                            const status = getStatusMeta(order);
                            const items = Array.isArray(order?.cart) ? order.cart : [];
                            const showPayButton = isPendingOrder(order);
                            return (
                                <article key={getOrderKey(order)} className={styles.orderCard}>
                                    <div className={styles.orderMain}>
                                        <div className={styles.orderContent}>
                                            <div className={styles.orderHeader}>
                                                <div className={styles.orderNumber}>{String(order?.orderNumber ?? '—')}</div>
                                                <div className={styles.orderDate}>от {formatOrderDate(order?.timestamp)}</div>
                                            </div>
                                            <div className={styles.statusRow}>
                                                <span className={`${styles.badge} ${styles[`badge_${status.tone}`]}`}>{status.label}</span>
                                                {/* <span className={styles.deliveryDate}>Дата доставки: {HARD_CODED_DELIVERY_DATE}</span> */}
                                            </div>
                                            <div className={styles.compositionTitle}>Состав заказа</div>
                                            <div className={styles.itemsRow} aria-label="Состав заказа">
                                                {items.map((item: any, index: number) => {
                                                    const imageCandidates = resolveOrderItemImages(item);
                                                    const src = imageCandidates.find((candidate) => !brokenImageUrls[candidate]) || '';
                                                    const itemName = typeof item?.name === 'string' && item.name.trim() ? item.name.trim() : 'Товар';
                                                    return src ? (
                                                        <img
                                                            key={`${String(item?.productId ?? index)}-${index}`}
                                                            className={styles.itemThumb}
                                                            src={src}
                                                            alt={itemName}
                                                            loading="lazy"
                                                            onError={() => handleImageError(src)}
                                                        />
                                                    ) : (
                                                        <div
                                                            key={`${String(item?.productId ?? index)}-${index}`}
                                                            className={styles.itemThumbStub}
                                                            aria-label={itemName}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className={styles.orderAside}>
                                            <button
                                                type="button"
                                                className={styles.detailsButton}
                                                onClick={() => handleOpenDetails(order)}
                                                aria-label={`Открыть подробности заказа ${String(order?.orderNumber ?? '')}`}
                                            >
                                                <img className={styles.detailsIcon} src="/icons/new/arrow_right.svg" alt="" aria-hidden="true" />
                                            </button>
                                            <div className={styles.sumBox}>
                                                <div className={styles.sumLabel}>Сумма</div>
                                                <div className={styles.sumValue}>{toMoney(order?.totalPrice)}</div>
                                            </div>
                                            {showPayButton ? (
                                                <button type="button" className={styles.payButton} onClick={() => void handlePrepareOrderForCart(order)}>
                                                    Оплатить
                                                </button>
                                            ) : (
                                                <div className={styles.payButtonPlaceholder} aria-hidden="true" />
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    {hasMoreOrders ? <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" /> : null}
                </>
            ) : null}
        </div>
    );
};

export default OrdersPanel;
